<app-custom-tabs 
  [tabs]="tabs" 
  [activeTabId]="activeTabId" 
  (tabClicked)="onTabChanged($event)">
</app-custom-tabs>

<div class="dashboard-container">

  <!-- Date Period Filter Bar -->
  <div class="period-bar">
    <div class="period-bar-top">
      <div class="period-info">
        <mat-icon class="period-info-icon">date_range</mat-icon>
        <span class="period-range-text">{{ getFormattedPeriodRange() }}</span>
        <span class="date-source-hint">
          <mat-icon class="hint-icon">info_outline</mat-icon>
          {{ getDateSourceHint() }}
        </span>
      </div>
      <div class="period-chips">
        <button *ngFor="let preset of periodPresets"
                class="period-chip"
                [class.active]="activePeriodPreset === preset.id"
                (click)="onPeriodChanged(preset.id)">
          {{ preset.label }}
        </button>
        <button class="period-chip custom-chip"
                [class.active]="activePeriodPreset === 'custom'"
                (click)="toggleCustomRange()">
          <mat-icon class="custom-chip-icon">edit_calendar</mat-icon>
          Custom
        </button>
      </div>
    </div>

    <div class="custom-range-row" *ngIf="showCustomRange">
      <mat-form-field appearance="outline" class="date-field">
        <mat-label>Start Date</mat-label>
        <input matInput [matDatepicker]="startPicker" [(ngModel)]="customStartDate">
        <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
        <mat-datepicker #startPicker></mat-datepicker>
      </mat-form-field>

      <mat-form-field appearance="outline" class="date-field">
        <mat-label>End Date</mat-label>
        <input matInput [matDatepicker]="endPicker" [(ngModel)]="customEndDate">
        <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
        <mat-datepicker #endPicker></mat-datepicker>
      </mat-form-field>

      <button mat-flat-button color="primary"
              class="apply-range-btn"
              (click)="onCustomDateApplied()"
              [disabled]="!customStartDate || !customEndDate">
        Apply Range
      </button>
    </div>
  </div>
  
  <!-- KPI Metrics Grid -->
  <div class="metrics-grid" *ngIf="activeTabId === 'services' && !servicesLoading">
    <mat-card class="metric-card shadow-sm">
      <mat-card-content>
        <div class="metric-header">
          <span class="metric-title">Completed Services</span>
          <mat-icon class="metric-icon primary-color">assignment_turned_in</mat-icon>
        </div>
        <div class="metric-value">{{ totalServicesCount }}</div>
        <div class="metric-subtitle">Total operations tracked</div>
      </mat-card-content>
    </mat-card>

    <mat-card class="metric-card shadow-sm">
      <mat-card-content>
        <div class="metric-header">
          <span class="metric-title">Total Base + Item Cost</span>
          <mat-icon class="metric-icon secondary-color">monetization_on</mat-icon>
        </div>
        <div class="metric-value cost-value">{{ formatCurrency(totalServicesCost) }}</div>
        <div class="metric-subtitle">Consumables and operations costs</div>
      </mat-card-content>
    </mat-card>

    <mat-card class="metric-card shadow-sm">
      <mat-card-content>
        <div class="metric-header">
          <span class="metric-title">Total Revenue</span>
          <mat-icon class="metric-icon accent-color">payments</mat-icon>
        </div>
        <div class="metric-value revenue-value">{{ formatCurrency(totalServicesRevenue) }}</div>
        <div class="metric-subtitle">Billed gross income</div>
      </mat-card-content>
    </mat-card>

    <mat-card class="metric-card shadow-sm">
      <mat-card-content>
        <div class="metric-header">
          <span class="metric-title">Net Profit Margin</span>
          <mat-icon class="metric-icon profit-color">trending_up</mat-icon>
        </div>
        <div class="metric-value profit-value" [ngClass]="getProfitClass(totalServicesProfit)">
          {{ formatCurrency(totalServicesProfit) }}
        </div>
        <div class="metric-subtitle">Retained earnings</div>
      </mat-card-content>
    </mat-card>
  </div>

  <div class="metrics-grid" *ngIf="activeTabId === 'products' && !productsLoading">
    <mat-card class="metric-card shadow-sm">
      <mat-card-content>
        <div class="metric-header">
          <span class="metric-title">Total Units Sold</span>
          <mat-icon class="metric-icon primary-color">inventory</mat-icon>
        </div>
        <div class="metric-value">{{ totalProductsSoldUnits }}</div>
        <div class="metric-subtitle">Actual units of medication sold</div>
      </mat-card-content>
    </mat-card>

    <mat-card class="metric-card shadow-sm">
      <mat-card-content>
        <div class="metric-header">
          <span class="metric-title">Total Cost of Sales (COGS)</span>
          <mat-icon class="metric-icon secondary-color">shopping_bag</mat-icon>
        </div>
        <div class="metric-value cost-value">{{ formatCurrency(totalProductsValuationCost) }}</div>
        <div class="metric-subtitle">Actual cost of medications sold</div>
      </mat-card-content>
    </mat-card>

    <mat-card class="metric-card shadow-sm">
      <mat-card-content>
        <div class="metric-header">
          <span class="metric-title">Actual Revenue</span>
          <mat-icon class="metric-icon accent-color">point_of_sale</mat-icon>
        </div>
        <div class="metric-value revenue-value">{{ formatCurrency(totalProductsValuationRevenue) }}</div>
        <div class="metric-subtitle">Total actual revenue from medications</div>
      </mat-card-content>
    </mat-card>

    <mat-card class="metric-card shadow-sm">
      <mat-card-content>
        <div class="metric-header">
          <span class="metric-title">Actual Profit Margin</span>
          <mat-icon class="metric-icon profit-color">query_stats</mat-icon>
        </div>
        <div class="metric-value profit-value" [ngClass]="getProfitClass(totalProductsValuationProfit)">
          {{ formatCurrency(totalProductsValuationProfit) }}
        </div>
        <div class="metric-subtitle">Retained profit from actual sales</div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Loading State Spinner -->
  <div class="loading-container" *ngIf="servicesLoading || productsLoading">
    <mat-spinner diameter="50"></mat-spinner>
    <div class="loading-text">Analyzing database and calculating profit margins...</div>
  </div>

  <!-- Data Display Card -->
  <mat-card class="data-card shadow-sm" *ngIf="!servicesLoading && !productsLoading">
    <mat-card-header class="data-header">
      <mat-card-title>
        {{ activeTabId === 'services' ? 'Completed Diagnostic & Treatment Margins' : 'Pharmacy Medications Sales Margins' }}
      </mat-card-title>
      <mat-card-subtitle>
        Detailed breakdown of Cost Prices, Selling Prices, and net Profit Margins calculated from underlying ChargeItemDefinitions.
      </mat-card-subtitle>
    </mat-card-header>

    <mat-divider></mat-divider>

    <mat-card-content class="data-content">
      
      <!-- Completed Services Table -->
      <div class="table-responsive" *ngIf="activeTabId === 'services'">
        <table mat-table [dataSource]="servicesRows" class="premium-table" *ngIf="servicesRows.length">
          
          <!-- Service Name Column -->
          <ng-container matColumnDef="serviceName">
            <th mat-header-cell *matHeaderCellDef>Product / Service</th>
            <td mat-cell *matCellDef="let row">
              <div class="service-cell">
                <div class="service-icon-wrapper">
                  <mat-icon class="service-type-icon">{{ getServiceTypeIcon(row.type) }}</mat-icon>
                </div>
                <div class="service-info">
                  <span class="service-name-text">{{ row.display }}</span>
                  <span class="service-code-sub">{{ row.code }} • {{ row.type | titlecase }}</span>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Patient Column -->
          <ng-container matColumnDef="patient">
            <th mat-header-cell *matHeaderCellDef>Patient</th>
            <td mat-cell *matCellDef="let row">
              <div class="patient-cell">
                <span class="patient-name">{{ row.patientName }}</span>
                <span class="patient-id">ID: {{ row.patientId }}</span>
              </div>
            </td>
          </ng-container>

          <!-- Cost Price Column -->
          <ng-container matColumnDef="costPrice">
            <th mat-header-cell *matHeaderCellDef class="align-right">Cost Price</th>
            <td mat-cell *matCellDef="let row" class="align-right">
              <span class="pill-badge gray-badge" 
                    [matTooltip]="'Base Cost: ' + formatCurrency(row.calcDetails.baseCost, row.currency) + '\nConsumables Cost: ' + formatCurrency(row.calcDetails.requiredConsumablesCost, row.currency)">
                {{ formatCurrency(row.costPrice, row.currency) }}
              </span>
            </td>
          </ng-container>

          <!-- Selling Price Column -->
          <ng-container matColumnDef="sellingPrice">
            <th mat-header-cell *matHeaderCellDef class="align-right">Selling Price</th>
            <td mat-cell *matCellDef="let row" class="align-right">
              <span class="pill-badge purple-badge">
                {{ formatCurrency(row.sellingPrice, row.currency) }}
              </span>
            </td>
          </ng-container>

          <!-- Profit Margin Column -->
          <ng-container matColumnDef="profitMargin">
            <th mat-header-cell *matHeaderCellDef class="align-right">Net Profit</th>
            <td mat-cell *matCellDef="let row" class="align-right">
              <span class="pill-badge" [ngClass]="getProfitClass(row.profit)">
                {{ formatCurrency(row.profit, row.currency) }}
                <mat-icon class="trend-icon" style="font-size: 14px; width: 14px; height: 14px; margin-left: 4px; vertical-align: middle;">
                  {{ row.profit > 0 ? 'arrow_upward' : (row.profit < 0 ? 'arrow_downward' : 'trending_flat') }}
                </mat-icon>
              </span>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="servicesDisplayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: servicesDisplayedColumns;" class="interactive-row"></tr>
        </table>

        <!-- Services Empty State -->
        <div class="empty-state-wrapper" *ngIf="servicesRows.length === 0">
          <div class="empty-icon-box">
            <mat-icon>payments</mat-icon>
          </div>
          <h3>No Completed Services Found</h3>
          <p>No completed service requests are currently registered in the database.</p>
        </div>
      </div>

      <!-- Pharmacy Products Table -->
      <div class="table-responsive" *ngIf="activeTabId === 'products'">
        <table mat-table [dataSource]="productsRows" class="premium-table" *ngIf="productsRows.length">
          
          <!-- Product Name Column -->
          <ng-container matColumnDef="productName">
            <th mat-header-cell *matHeaderCellDef>Medication / Product</th>
            <td mat-cell *matCellDef="let row">
              <div class="product-cell">
                <div class="product-icon-wrapper">
                  <mat-icon class="product-type-icon">medication</mat-icon>
                </div>
                <div class="product-info">
                  <span class="product-name-text">{{ row.name }}</span>
                  <span class="product-subtext">
                    {{ row.form | titlecase }} • {{ row.manufacturer | na }}
                  </span>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Stock Level Column -->
          <ng-container matColumnDef="stock">
            <th mat-header-cell *matHeaderCellDef class="align-center">Units Sold</th>
            <td mat-cell *matCellDef="let row" class="align-center">
              <span class="pill-badge gray-badge">
                {{ row.unitsSold }} units
              </span>
            </td>
          </ng-container>

          <!-- Cost Price Column -->
          <ng-container matColumnDef="costPrice">
            <th mat-header-cell *matHeaderCellDef class="align-right">Total Cost</th>
            <td mat-cell *matCellDef="let row" class="align-right">
              <span class="pill-badge gray-badge">
                {{ formatCurrency(row.costPrice, row.currency) }}
              </span>
            </td>
          </ng-container>

          <!-- Selling Price Column -->
          <ng-container matColumnDef="sellingPrice">
            <th mat-header-cell *matHeaderCellDef class="align-right">Actual Revenue</th>
            <td mat-cell *matCellDef="let row" class="align-right">
              <span class="pill-badge purple-badge">
                {{ formatCurrency(row.sellingPrice, row.currency) }}
              </span>
            </td>
          </ng-container>

          <!-- Profit Margin Column -->
          <ng-container matColumnDef="profitMargin">
            <th mat-header-cell *matHeaderCellDef class="align-right">Actual Profit</th>
            <td mat-cell *matCellDef="let row" class="align-right">
              <span class="pill-badge" [ngClass]="getProfitClass(row.profit)">
                {{ formatCurrency(row.profit, row.currency) }}
              </span>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="productsDisplayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: productsDisplayedColumns;" class="interactive-row"></tr>
        </table>

        <!-- Products Empty State -->
        <div class="empty-state-wrapper" *ngIf="productsRows.length === 0">
          <div class="empty-icon-box">
            <mat-icon>inventory</mat-icon>
          </div>
          <h3>No Medications Configured</h3>
          <p>There are no registered pharmacy products in the terminology database yet.</p>
        </div>
      </div>

    </mat-card-content>
  </mat-card>
</div>
